import { Link } from "react-router-dom";
import { Seo, ORG_LD, MATTHEW_LD } from "../lib/seo";
import { MaskedLine, Reveal, FadeIn } from "../components/Reveal";
import { Eyebrow, ArrowLink } from "../components/atoms";
import { FaqAccordion } from "../components/FaqAccordion";
import {
  CASES,
  HOME_FAQS,
  MATTHEW,
  MATTHEW_PHOTO,
  CALENDLY_URL,
} from "../data/content";

// Bright editorial homepage (owner-directed): warm-white hero, large slate
// editorial-sans headline left, compact portrait + 3-line caption right, and
// four editorial service columns immediately beneath. No min-height:100vh,
// no italic gold second line, no ornamental photo frame, no card grid boxes.
const SERVICES = [
  {
    key: "buyer-advocacy",
    title: "Buyer Advocacy",
    desc: "Know where and what to buy for your circumstances, with someone representing you.",
    cta: "Explore Buyer Advocacy",
    to: "/buyer-advocacy",
  },
  {
    key: "suburbiq",
    title: "SuburbIQ",
    desc: "Understand a suburb's strengths, risks and fit before you buy.",
    cta: "View SuburbIQ reports",
    to: "/suburbiq",
  },
  {
    key: "property-portfolio-assessment",
    title: "Property & Portfolio Assessment",
    desc: "Understand what you own, what it costs to hold, and your options.",
    cta: "Explore the Assessment",
    to: "/property-portfolio-assessment",
  },
  {
    key: "vendor-advocacy",
    title: "Vendor Advocacy",
    desc: "Choose your selling agent and navigate the sale with professional support.",
    cta: "Explore Vendor Advocacy",
    to: "/vendor-advocacy",
  },
];

const Hero = () => (
  <section className="bg-herowhite text-slatehero" data-testid="hero">
    <div className="max-w-[1280px] mx-auto px-6 lg:px-16 pt-10 pb-8 lg:pt-10 lg:pb-8">
      {/* Eyebrow sits ABOVE the grid so it never offsets the photo from the
          headline (owner direction 1 Nov 2026). */}
      <FadeIn delay={0.1}>
        <p
          data-testid="hero-eyebrow"
          className="text-sm font-bold tracking-[0.22em] uppercase text-slatehero/70"
        >
          Independent Property Advice &amp; Advocacy
        </p>
      </FadeIn>

      {/*
        Balanced two-column hero. DOM order is copy+CTA → portrait+credentials,
        so on narrow screens the stack is copy, portrait, then the service
        chooser (the photo can never fall below it) — ONE portrait element, no
        duplicates, no absolute positioning, no arbitrary offsets.
      */}
      <div className="mt-6 lg:mt-8 grid grid-cols-1 lg:grid-cols-2 gap-y-10 lg:gap-x-16 items-start">
        {/* LEFT — outcome, support, one primary action */}
        <div>
          <h1
            className="font-display font-bold text-slatehero tracking-tight leading-[1.12] text-[clamp(2.5rem,4.3vw,3.9rem)]"
            data-testid="hero-h1"
          >
            <MaskedLine delay={0.25}>Smarter property decisions.</MaskedLine>
            <MaskedLine delay={0.37}>Backed by 80+ metrics.</MaskedLine>
          </h1>
          <FadeIn delay={0.7}>
            <p
              className="mt-7 text-lg lg:text-xl leading-[1.5] text-slatehero/80"
              data-testid="hero-subhead"
            >
              Should you buy, hold or sell? Understand your options, the risks and what makes sense for your circumstances.
            </p>
            <div className="mt-9 flex flex-wrap items-center gap-x-8 gap-y-5">
              <a
                href={CALENDLY_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-book-call"
                className="inline-flex items-center justify-center px-8 py-4 bg-charcoal text-white text-base font-semibold transition-[background-color] duration-300 hover:bg-slatehero focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-2 focus-visible:ring-offset-herowhite"
              >
                Book my 15-minute call
              </a>
              <a
                href="#services"
                data-testid="hero-explore-services"
                onClick={(e) => {
                  const el = document.getElementById("services");
                  if (!el) return; // fall back to native anchor jump
                  e.preventDefault();
                  const reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
                  el.scrollIntoView({ behavior: reduced ? "auto" : "smooth", block: "start" });
                  el.setAttribute("tabindex", "-1");
                  el.focus({ preventScroll: true });
                }}
                className="text-base font-semibold text-slatehero underline-offset-4 transition-colors duration-300 hover:text-golddark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold"
              >
                Explore our services <span aria-hidden="true">→</span>
              </a>
            </div>
            <p className="mt-8 text-base text-slatehero/65" data-testid="hero-independence-line">
              Independent advice. No selling-agent commissions.
            </p>
          </FadeIn>
        </div>

        {/* RIGHT — ONE portrait+caption block, top-aligned with the H1 (grid
            items-start). Right-aligned within its column on desktop so its
            outer right edge meets the shared grid edge; caption left-aligned
            beneath the photo. Natural image proportions — no cropping. */}
        <FadeIn delay={0.5}>
          <div data-testid="hero-authority" className="lg:ml-auto lg:w-fit">
            <img
              src={MATTHEW_PHOTO}
              alt="Matthew Clark, Founder & Head of Client Strategy at 80 Metrics, holding his two bestselling books on property investment"
              className="block h-auto w-[260px] sm:w-[300px] xl:w-[320px] max-w-full"
              data-testid="hero-portrait"
            />
            {/* Caption hierarchy: 16px photo→name, 6px name→role, 12px
                role→quals. Full slate contrast (no faint greys); diplomas
                share one line where they fit, wrap only BETWEEN whole units;
                QPIA always on its own line. */}
            <div className="mt-4">
              <p className="font-display text-2xl font-semibold text-slatehero" data-testid="hero-name">{MATTHEW.name}</p>
              <p className="mt-1.5 text-base text-slatehero">{MATTHEW.role}</p>
              <div className="mt-3 text-[15px] sm:text-base leading-[1.5] text-slatehero/90" data-testid="hero-quals">
                <p>
                  <span className="whitespace-nowrap">Dip. Prop. (Agency Mgmt)</span>
                  <span className="hidden sm:inline" aria-hidden="true"> · </span>
                  <span className="block sm:inline sm:whitespace-nowrap">Dip. Fin. Services</span>
                </p>
                <p className="mt-1 font-semibold text-slatehero">QPIA</p>
              </div>
            </div>
          </div>
        </FadeIn>
      </div>
    </div>
  </section>
);

// Four lightly-outlined service cards (owner final polish): mixed-case
// heading, warm-white surfaces, restrained gold accent, ~24px internal
// padding and a consistent ~20–24px gap. Four columns only at xl (1280+)
// where they fit comfortably; two columns on tablet/narrow desktop, one on
// phones. Subgrid rows keep heading tops, description starts and action
// baselines aligned per row — equal heights, no fixed tall empty gaps. Each
// card is ONE semantic link (whole card clickable, visible focus ring).
const ServicePaths = () => (
  <section id="services" className="bg-herowhite scroll-mt-28" data-testid="service-paths" aria-labelledby="service-paths-heading">
    <div className="max-w-[1280px] mx-auto px-6 lg:px-16 pt-6 pb-6 lg:pt-8 lg:pb-10">
      <Reveal>
        <h2
          id="service-paths-heading"
          className="font-display text-2xl lg:text-[32px] font-semibold tracking-tight text-slatehero"
          data-testid="service-paths-heading"
        >
          What would you like help with?
        </h2>
      </Reveal>
      <div className="mt-7 grid grid-cols-1 gap-5 sm:grid-cols-2 sm:[grid-template-rows:auto_auto] xl:grid-cols-4 xl:gap-6 xl:[grid-template-rows:auto]">
        {SERVICES.map((s) => (
          <Link
            key={s.key}
            to={s.to}
            data-testid={`choice-${s.key}`}
            aria-label={`${s.cta} — ${s.title}`}
            className="group flex flex-col sm:row-span-2 sm:grid sm:grid-rows-subgrid rounded-md border border-charcoal/15 bg-white p-6 transition-[border-color,background-color] duration-300 hover:border-gold/70 hover:bg-ivory focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-2 focus-visible:ring-offset-herowhite"
          >
            <div>
              <span className="mb-4 block h-[3px] w-10 bg-gold" aria-hidden="true" />
              <h3 className="font-display text-xl lg:text-[22px] font-semibold leading-[1.25] text-slatehero transition-colors duration-300 group-hover:text-golddark">
                {s.title}
              </h3>
              <p className="mt-3 text-base leading-[1.5] text-slatehero/85">{s.desc}</p>
            </div>
            <span className="mt-5 mt-auto sm:mt-0 sm:self-end inline-flex items-center gap-2 text-base font-semibold text-charcoal transition-colors duration-300 group-hover:text-golddark">
              {s.cta} <span className="inline-block transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true">→</span>
            </span>
          </Link>
        ))}
      </div>
    </div>
  </section>
);

const FRAMEWORK = [
  { num: "01", name: "Understand", body: "Your goals. The market. Your options.", links: [{ to: "/suburbiq", label: "SuburbIQ" }] },
  { num: "02", name: "Decide", body: "Weigh the evidence and trade-offs.", links: [{ to: "/property-portfolio-assessment", label: "Property & Portfolio Assessment" }] },
  {
    num: "03",
    name: "Act",
    body: "Buy, sell or retain — with the right support.",
    links: [
      { to: "/buyer-advocacy", label: "Buyer Advocacy" },
      { to: "/vendor-advocacy", label: "Vendor Advocacy" },
    ],
  },
];

const Framework = () => (
  <section className="bg-white" data-testid="framework-section">
    <div className="max-w-[1280px] mx-auto px-6 lg:px-16 pt-4 pb-6 lg:pt-10 lg:pb-10">
      <Reveal>
        <Eyebrow>The framework</Eyebrow>
        <h2 className="mt-6 font-serif text-3xl sm:text-4xl lg:text-6xl font-medium tracking-tight text-charcoal">
          Understand. Decide. Act.
        </h2>
        <p className="mt-6 text-base lg:text-lg text-charcoal/65 max-w-2xl">
          Understand your position. Decide with evidence. Act only when it makes sense.
        </p>
      </Reveal>
      <div className="mt-10 lg:mt-14 grid grid-cols-1 md:grid-cols-3 gap-10 lg:gap-14">
        {FRAMEWORK.map((s, i) => (
          <Reveal key={s.num} delay={i * 0.1} className="border-t-2 border-charcoal pt-8">
            <span className="font-serif text-5xl lg:text-6xl font-medium text-gold">{s.num}</span>
            <h3 className="mt-5 text-xs font-semibold tracking-[0.3em] uppercase text-charcoal">{s.name}</h3>
            <p className="mt-4 text-sm lg:text-base text-charcoal/65 leading-relaxed">{s.body}</p>
            <div className="mt-6 flex flex-col items-start gap-3">
              {s.links.map((l) => (
                <ArrowLink
                  key={l.to}
                  to={l.to}
                  data-testid={`framework-${s.num}-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}-link`}
                >
                  {l.label}
                </ArrowLink>
              ))}
            </div>
          </Reveal>
        ))}
      </div>
      <Reveal delay={0.15}>
        <p className="mt-14 border border-gold/50 bg-bone px-6 py-5 text-sm text-charcoal/80 max-w-2xl" data-testid="framework-optional-note">
          <span className="font-semibold text-golddark">Note —</span> SuburbIQ and the Assessment are optional. You can go directly to advocacy.
        </p>
      </Reveal>
    </div>
  </section>
);

const Cases = () => (
  <section className="bg-bone" data-testid="cases-section">
    <div className="max-w-[1280px] mx-auto px-6 lg:px-16 pt-4 pb-6 lg:pt-10 lg:pb-10">
      <Reveal>
        <Eyebrow>Decisions in practice</Eyebrow>
        <h2 className="mt-6 font-serif text-3xl sm:text-4xl lg:text-6xl font-medium tracking-tight text-charcoal">
          Evidence behind the decision.
        </h2>
        <p className="mt-6 text-base lg:text-lg text-charcoal/65 max-w-2xl">
          The right result depends on the evidence — not on completing a transaction.
        </p>
      </Reveal>
      <div className="mt-10 lg:mt-14 grid grid-cols-1 md:grid-cols-2 gap-px bg-charcoal/15 border border-charcoal/15">
        {CASES.map((c, i) => (
          <Reveal key={c.label} delay={i * 0.07} className="bg-ivory p-8 lg:p-12">
            <span className="text-xs font-semibold tracking-[0.3em] uppercase text-golddark">{c.label}</span>
            <h3 className="mt-4 font-serif text-2xl lg:text-3xl font-medium leading-snug text-charcoal">{c.title}</h3>
            <p className="mt-4 text-sm leading-relaxed text-charcoal/65">{c.body}</p>
          </Reveal>
        ))}
      </div>
      <Reveal delay={0.15}>
        <p className="mt-10 text-xs leading-relaxed text-charcoal/50 max-w-2xl">
          Individual outcomes vary. Recommendations depend on the client’s circumstances and the evidence available at the time.
        </p>
      </Reveal>
    </div>
  </section>
);

const FaqPreview = () => (
  <section className="bg-white" data-testid="faq-preview-section">
    <div className="max-w-[1280px] mx-auto px-6 lg:px-16 pt-4 pb-6 lg:pt-10 lg:pb-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
      <Reveal className="lg:col-span-4">
        <Eyebrow>FAQ</Eyebrow>
        <h2 className="mt-6 font-serif text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-charcoal">
          Property advice, explained.
        </h2>
        <p className="mt-6 text-sm lg:text-base text-charcoal/65 leading-relaxed">
          Clear answers to the questions clients ask before they engage us.
        </p>
        <div className="mt-8">
          <ArrowLink to="/faqs" data-testid="faq-view-all-link">View all property advisory FAQs</ArrowLink>
        </div>
      </Reveal>
      <Reveal delay={0.1} className="lg:col-span-8">
        <FaqAccordion items={HOME_FAQS} testidPrefix="home-faq" />
      </Reveal>
    </div>
  </section>
);

const FinalCta = () => (
  <section className="bg-charcoal text-ivory" data-testid="final-cta-section">
    <div className="max-w-[1280px] mx-auto px-6 lg:px-16 pt-6 pb-16 lg:pt-10 lg:pb-24">
      <Reveal>
        <Eyebrow dark>Evidence over opinion</Eyebrow>
        <h2 className="mt-6 font-serif text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight leading-[1.05] max-w-4xl">
          Before your next property move, talk to 80 Metrics.
        </h2>
        <p className="mt-8 text-base lg:text-lg text-ivory/65 max-w-xl leading-relaxed">
          A short introductory call with Matthew to identify your next step. Scope and fees are agreed before any paid work begins.
        </p>
        <div className="mt-10">
          <a
            href={CALENDLY_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="final-cta-button"
            className="inline-flex items-center justify-center px-8 py-4 bg-gold text-ink text-xs font-semibold tracking-[0.2em] uppercase transition-[background-color] duration-300 hover:bg-ivory"
          >
            Book my 15-minute call
          </a>
        </div>
      </Reveal>
    </div>
  </section>
);

export default function Home() {
  return (
    <>
      <Seo
        title="80 Metrics Property Advisory — Buy better. Sell smarter. Know what to keep."
        description="Understand what 80+ property metrics mean for your next move. Independent buyer advocacy, vendor advocacy, SuburbIQ suburb research and property & portfolio assessment."
        jsonLd={[ORG_LD, MATTHEW_LD]}
      />
      <Hero />
      <ServicePaths />
      <Cases />
      <Framework />
      <FaqPreview />
      <FinalCta />
    </>
  );
}
